import React, { useState, useEffect } from 'react';
import {
    FaFileAlt,
    FaFileContract,
    FaUser,
    FaFilePdf,
    FaFileWord,
    FaDownload,
    FaCheckCircle,
    FaTimesCircle,
    FaHourglassHalf,
    FaEye,
    FaSpinner,
    FaCalendarAlt,
    FaUserTie,
    FaBook,
    FaGraduationCap,
    FaSearch,
    FaEdit
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';
import * as authService from '../services/authService';
import * as reviewerService from '../services/reviewerService';
import * as masterService from '../services/masterService';

const AssignedAbstract = () => {
    const [assignedAbstracts, setAssignedAbstracts] = useState([]);
    const [filteredAbstracts, setFilteredAbstracts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAbstract, setSelectedAbstract] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewComment, setReviewComment] = useState('');
    const [actionType, setActionType] = useState(''); // 'accept_for_review', 'accept', 'reject'
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [toast, setToast] = useState(null);
    const navigate = useNavigate();

    // Status options
    const statusOptions = [
        { value: 'all', label: 'All Abstracts' },
        { value: 'in_queue', label: 'In Queue' },
        { value: 'in_review', label: 'In Review' },
        { value: 'accepted', label: 'Accepted' },
        { value: 'rejected', label: 'Rejected' }
    ];

    useEffect(() => {
        const currentUser = authService.getCurrentUser();
        const isVerifiedReviewer = authService.isVerifiedReviewer(currentUser);
        const isVerifiedEditorial = authService.isVerifiedEditorial(currentUser);

        // Check if user is verified reviewer or editorial board
        if (!currentUser || (!isVerifiedReviewer && !isVerifiedEditorial)) {
            navigate('/');
            return;
        }

        loadAssignedAbstracts();
    }, [navigate]);

    const loadAssignedAbstracts = async () => {
        setLoading(true);
        try {
            const [requestsResponse, assignmentsResponse, streamsRes, specsRes] = await Promise.all([
                reviewerService.getReviewerRequests(),
                reviewerService.getReviewerAssignments(),
                masterService.getAllStreams().catch(() => ({ data: [] })),
                masterService.getAllSpecializations().catch(() => ({ data: [] }))
            ]);

            const requestsData = requestsResponse.data || requestsResponse || [];
            const assignmentsData = assignmentsResponse.data || assignmentsResponse || [];

            const streamsData = streamsRes.data || streamsRes || [];
            const specsData = specsRes.data || specsRes || [];

            const tempStreamsMap = {};
            (Array.isArray(streamsData) ? streamsData : []).forEach(s => { if (s.id) tempStreamsMap[s.id] = s.name; });

            const tempSpecsMap = {};
            (Array.isArray(specsData) ? specsData : []).forEach(s => { if (s.id) tempSpecsMap[s.id] = s.name; });

            const allData = [...requestsData, ...assignmentsData];

            // Filter out any potential duplicates based on ID
            const uniqueDataMap = new Map();
            allData.forEach(item => {
                if (item && item.id) uniqueDataMap.set(item.id, item);
            });
            const data = Array.from(uniqueDataMap.values());


            // Map API response to UI model
            const mappedData = data.map(item => {
                const sub = item.submission || {};
                return {
                    id: sub.id || item.submission_id || item.id,
                    assignmentId: item.id,
                    abstractTitle: sub.title || 'Untitled',
                    subject: sub.subject?.name || 'N/A',
                    major: sub.stream?.name || tempStreamsMap[sub.stream_id] || tempStreamsMap[sub.stream] || 'N/A',
                    specialization: sub.specialization?.name || tempSpecsMap[sub.specialization_id] || tempSpecsMap[sub.specialization] || 'N/A',
                    userName: (() => {
                        const submitterName = sub.submitter?.full_name || sub.submitter?.name || sub.user?.full_name || sub.user?.name;
                        const extra = sub.authors || sub.co_authors || [];
                        let extraNames = [];
                        try {
                            const parsed = typeof extra === 'string' ? JSON.parse(extra) : extra;
                            const arr = Array.isArray(parsed) ? parsed : [parsed];
                            extraNames = arr.map(a => a ? (typeof a === 'string' ? a : (a.full_name || a.name)) : null).filter(Boolean);
                        } catch (e) {
                            extraNames = typeof extra === 'string' ? extra.split(',').map(s => s.trim()) : [];
                        }

                        const allNames = [submitterName, ...extraNames].filter(Boolean);
                        return allNames.length > 0 ? allNames.join(', ') : 'Author';
                    })(),
                    status: (() => {
                        const assignStatus = (item.status || '').toLowerCase();
                        const subStatus = (sub.status || '').toLowerCase();

                        if (assignStatus === 'accepted') {
                            if (subStatus === 'modification_requested') return 'modification_requested';
                            if (subStatus === 'resubmitted') return 'resubmitted';
                            if (['under_review', 'resubmitted'].includes(subStatus)) return 'in_review';

                            // If submission is already final
                            if (subStatus === 'accepted') return 'accepted';
                            if (subStatus === 'rejected') return 'rejected';

                            return 'in_review'; // Default for accepted assignment
                        }

                        return assignStatus || subStatus || 'pending';
                    })(),
                    submittedAt: sub.created_at || sub.submission_date || '',
                    inQueueAt: item.created_at || '',
                    uploadedFile: (() => {
                        const fileUrl = sub.file_url || sub.versions?.[0]?.file_url || null;
                        return fileUrl ? {
                            name: fileUrl.split('/').pop(),
                            url: fileUrl
                        } : null;
                    })(),
                    abstractText: sub.abstract_text || '',
                    keywords: sub.keywords || '',
                    authors: sub.co_authors || [],
                    guideDetails: sub.guide_details || {},
                    university: sub.university || ''
                };
            });

            // Sort by newest assignment first
            const sortedAbstracts = [...mappedData].sort((a, b) => {
                const dateA = new Date(a.inQueueAt || a.submittedAt);
                const dateB = new Date(b.inQueueAt || b.submittedAt);
                return dateB - dateA;
            });

            setAssignedAbstracts(sortedAbstracts);
            setFilteredAbstracts(sortedAbstracts);
        } catch (error) {
            console.error('Error loading assigned abstracts:', error);
            showToast('Failed to load assigned abstracts', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let results = assignedAbstracts;

        if (statusFilter !== 'all') {
            results = results.filter(abstract => abstract.status === statusFilter);
        }

        if (searchTerm.trim() !== '') {
            const term = searchTerm.toLowerCase();
            results = results.filter(abstract =>
                abstract.abstractTitle.toLowerCase().includes(term) ||
                abstract.subject.toLowerCase().includes(term) ||
                abstract.major.toLowerCase().includes(term) ||
                (abstract.userName && abstract.userName.toLowerCase().includes(term))
            );
        }

        setFilteredAbstracts(results);
    }, [searchTerm, statusFilter, assignedAbstracts]);

    const getStatusConfig = (status) => {
        switch (status) {
            case 'pending':
            case 'reviewer_assigned':
            case 'assigned':
            case 'in_queue':
                return {
                    label: 'In Queue',
                    bgColor: 'bg-orange-100',
                    textColor: 'text-orange-700',
                    icon: FaHourglassHalf,
                    borderColor: 'border-orange-500'
                };
            case 'in_review':
                return {
                    label: 'In Review',
                    bgColor: 'bg-yellow-100',
                    textColor: 'text-yellow-700',
                    icon: FaSpinner,
                    borderColor: 'border-yellow-500'
                };
            case 'accepted':
                return {
                    label: 'Accepted',
                    bgColor: 'bg-green-100',
                    textColor: 'text-green-700',
                    icon: FaCheckCircle,
                    borderColor: 'border-green-500'
                };
            case 'rejected':
                return {
                    label: 'Rejected',
                    bgColor: 'bg-red-100',
                    textColor: 'text-red-700',
                    icon: FaTimesCircle,
                    borderColor: 'border-red-500'
                };
            case 'modification_requested':
                return {
                    label: 'Sent for Modification',
                    bgColor: 'bg-amber-100',
                    textColor: 'text-amber-700',
                    icon: FaEdit,
                    borderColor: 'border-amber-500'
                };
            case 'resubmitted':
                return {
                    label: 'In Review',
                    bgColor: 'bg-yellow-100',
                    textColor: 'text-yellow-700',
                    icon: FaSpinner,
                    borderColor: 'border-yellow-500'
                };
            default:
                return {
                    label: 'Submitted',
                    bgColor: 'bg-blue-100',
                    textColor: 'text-blue-700',
                    icon: FaHourglassHalf,
                    borderColor: 'border-blue-500'
                };
        }
    };

    const getFileIcon = (fileName) => {
        if (!fileName) return null;
        const extension = fileName.split('.').pop().toLowerCase();
        if (extension === 'pdf') {
            return <FaFilePdf className="text-red-500 text-xl" />;
        } else if (extension === 'doc' || extension === 'docx') {
            return <FaFileWord className="text-blue-500 text-xl" />;
        }
        return <FaFileAlt className="text-gray-500 text-xl" />;
    };

    const isDownloadableFile = (fileName) => {
        if (!fileName) return false;
        const extension = fileName.split('.').pop().toLowerCase();
        return extension === 'pdf' || extension === 'doc' || extension === 'docx';
    };

    const handleDownloadFile = (abstract) => {
        if (abstract.uploadedFile && abstract.uploadedFile.url) {
            try {
                const link = document.createElement('a');
                link.href = abstract.uploadedFile.url;
                link.download = abstract.uploadedFile.name;
                link.target = "_blank";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                showToast(`Downloading ${abstract.uploadedFile.name}`, 'success');
            } catch (error) {
                console.error('Download error:', error);
                showToast('Could not download file. Please try again.', 'error');
            }
        } else {
            showToast('No file available for download', 'error');
        }
    };

    const handlePreviewClick = (abstract) => {
        setSelectedAbstract(abstract);
        setShowPreviewModal(true);
    };

    const handleActionClick = (abstract, action) => {
        setSelectedAbstract(abstract);
        setActionType(action);
        setReviewComment('');

        if (action === 'accept' || action === 'reject' || action === 'modify') {
            setShowReviewModal(true);
        } else {
            confirmAction();
        }
    };

    const confirmAction = async () => {
        if (!selectedAbstract || !actionType) return;

        try {
            if (actionType === 'accept_for_review') {
                await reviewerService.respondToAssignment({
                    assignment_id: selectedAbstract.assignmentId,
                    accept: true
                });
            } else if (actionType === 'reject_for_review') {
                await reviewerService.respondToAssignment({
                    assignment_id: selectedAbstract.assignmentId,
                    accept: false
                });
            } else if (actionType === 'accept' || actionType === 'reject' || actionType === 'modify') {
                if (!reviewComment.trim()) {
                    showToast('Please provide comments/reason', 'error');
                    return;
                }

                const decision = actionType === 'accept' ? 'ACCEPT'
                    : (actionType === 'reject' ? 'REJECT' : 'MODIFY');

                await reviewerService.submitReview({
                    assignment_id: selectedAbstract.assignmentId,
                    decision,
                    comments: reviewComment
                });
            }

            loadAssignedAbstracts();
            setShowReviewModal(false);
            setSelectedAbstract(null);
            setActionType('');
            setReviewComment('');

            const actionText = actionType === 'accept_for_review' ? 'accepted for review'
                : (actionType === 'reject_for_review' ? 'rejected for review'
                    : (actionType === 'accept' ? 'accepted' : (actionType === 'reject' ? 'rejected' : 'sent for modification')));
            showToast(`Abstract has been ${actionText} successfully`, 'success');
        } catch (error) {
            console.error('Error performing action:', error);
            showToast(error.message || 'Failed to process request', 'error');
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getTimeSinceAssignment = (abstract) => {
        if (!abstract.inQueueAt) return 'N/A';

        const assignedDate = new Date(abstract.inQueueAt);
        const now = new Date();
        const diffMs = now - assignedDate;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
            return diffHours < 1 ? 'Less than an hour' : `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        }

        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    };

    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter('all');
    };

    return (
        <>
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            {/* Preview Modal */}
            {showPreviewModal && selectedAbstract && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto">
                        <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-6 rounded-t-2xl sticky top-0 z-10">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h2 className="text-2xl font-bold text-white mb-2">Abstract Preview</h2>
                                    <p className="text-purple-100">{selectedAbstract.abstractTitle}</p>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowPreviewModal(false);
                                        setSelectedAbstract(null);
                                    }}
                                    className="text-white hover:bg-white/20 p-2 rounded-lg transition-all"
                                >
                                    ✕
                                </button>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="mb-8">
                                <h3 className="text-lg font-bold text-gray-900 mb-4">Abstract Content</h3>

                                {selectedAbstract.uploadedFile ? (
                                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border-2 border-purple-200 mb-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-3">
                                                {getFileIcon(selectedAbstract.uploadedFile.name)}
                                                <div>
                                                    <p className="font-semibold text-gray-900">{selectedAbstract.uploadedFile.name}</p>
                                                    <p className="text-sm text-gray-600">Attached File</p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleDownloadFile(selectedAbstract)}
                                                className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-pink-500 transition-all font-bold"
                                            >
                                                <FaDownload />
                                                Download
                                            </button>
                                        </div>
                                    </div>
                                ) : null}

                                {selectedAbstract.abstractText ? (
                                    <div className="bg-white p-6 rounded-xl border-2 border-gray-100 shadow-inner max-h-[400px] overflow-y-auto">
                                        <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                                            {selectedAbstract.abstractText}
                                        </p>
                                    </div>
                                ) : (!selectedAbstract.uploadedFile && (
                                    <div className="text-center py-12 bg-gray-50 rounded-xl">
                                        <FaFileContract className="text-6xl text-gray-300 mx-auto mb-4" />
                                        <p className="text-gray-500 font-medium">No content available for this submission.</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 rounded-b-2xl">
                            <button
                                onClick={() => {
                                    setShowPreviewModal(false);
                                    setSelectedAbstract(null);
                                }}
                                className="w-full px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                            >
                                Close Preview
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Review Modal */}
            {showReviewModal && selectedAbstract && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fade-in">
                        <div className="text-center mb-6">
                            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${actionType === 'accept' ? 'bg-green-100' : (actionType === 'reject' ? 'bg-red-100' : 'bg-yellow-100')
                                }`}>
                                {actionType === 'accept' ? (
                                    <FaCheckCircle className="w-8 h-8 text-green-600" />
                                ) : actionType === 'reject' ? (
                                    <FaTimesCircle className="w-8 h-8 text-red-600" />
                                ) : (
                                    <FaEdit className="w-8 h-8 text-yellow-600" />
                                )}
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                {actionType === 'accept' ? 'Accept Abstract' : (actionType === 'reject' ? 'Reject Abstract' : 'Request Modifications')}
                            </h3>
                            <p className="text-gray-600 mb-4">
                                {actionType === 'accept'
                                    ? 'Please provide comments for this acceptance:'
                                    : actionType === 'reject'
                                        ? 'Please provide reason for rejection:'
                                        : 'Please specify the required modifications:'}
                            </p>
                            <div className="bg-gray-50 p-3 rounded-lg mb-4">
                                <p className="text-sm font-semibold text-gray-900">{selectedAbstract.abstractTitle}</p>
                            </div>
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                {actionType === 'accept' ? 'Acceptance Comments' : (actionType === 'reject' ? 'Rejection Reason' : 'Modification Comments')}
                            </label>
                            <textarea
                                value={reviewComment}
                                onChange={(e) => setReviewComment(e.target.value)}
                                rows="4"
                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                                placeholder={actionType === 'accept'
                                    ? 'Enter your comments...'
                                    : actionType === 'reject'
                                        ? 'Enter the reason for rejection...'
                                        : 'Describe the modifications needed...'
                                }
                                required
                            />
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowReviewModal(false);
                                    setActionType('');
                                    setReviewComment('');
                                }}
                                className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmAction}
                                className={`flex-1 px-4 py-3 rounded-lg font-semibold text-white transition-all ${actionType === 'accept'
                                    ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700'
                                    : actionType === 'reject'
                                        ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
                                        : 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700'
                                    }`}
                            >
                                {actionType === 'accept' ? 'Confirm Acceptance' : (actionType === 'reject' ? 'Confirm Rejection' : 'Submit Request')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50 py-6">
                <div className="max-w mx-auto px-4">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl shadow-lg p-6 mb-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">Assigned Abstracts</h1>
                                <p className="text-purple-100">Review abstracts assigned to you</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                    <FaFileContract className="text-2xl text-white" />
                                </div>
                                <div className="text-right">
                                    <p className="text-white text-sm">Total Assigned</p>
                                    <p className="text-2xl font-bold text-white">{assignedAbstracts.length}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Filters and Search */}
                    <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Search Abstracts</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="Search by title, subject, author..."
                                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                                    />
                                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Filter by Status</label>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all bg-white"
                                >
                                    {statusOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Loading State */}
                    {loading ? (
                        <div className="flex justify-center items-center h-64">
                            <FaSpinner className="text-4xl text-purple-500 animate-spin" />
                        </div>
                    ) : (
                        <>
                            <div className="mb-4">
                                <p className="text-gray-600">
                                    Showing <span className="font-bold">{filteredAbstracts.length}</span> of{' '}
                                    <span className="font-bold">{assignedAbstracts.length}</span> assigned abstracts
                                </p>
                            </div>

                            {filteredAbstracts.length === 0 ? (
                                <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                                    <FaFileContract className="text-6xl text-gray-300 mx-auto mb-4" />
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                        {searchTerm || statusFilter !== 'all' ? 'No Matching Abstracts' : 'No Assigned Abstracts'}
                                    </h3>
                                    <p className="text-gray-600 mb-4">
                                        {searchTerm || statusFilter !== 'all'
                                            ? 'Try adjusting your search or filter criteria'
                                            : 'You have no abstracts assigned for review yet.'}
                                    </p>
                                    {(searchTerm || statusFilter !== 'all') && (
                                        <button
                                            onClick={clearFilters}
                                            className="mt-4 px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-pink-500 transition-all"
                                        >
                                            Clear Filters
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 gap-6">
                                    {filteredAbstracts.map((abstract) => {
                                        const statusConfig = getStatusConfig(abstract.status);
                                        const StatusIcon = statusConfig.icon;

                                        const canAcceptForReview = abstract.status === 'pending' || abstract.status === 'reviewer_assigned' || abstract.status === 'in_queue' || abstract.status === 'assigned';
                                        const canAcceptOrReject = abstract.status === 'in_review' || abstract.status === 'resubmitted';

                                        return (
                                            <div
                                                key={abstract.id}
                                                className={`bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all border-l-4 ${statusConfig.borderColor}`}
                                            >
                                                <div className="p-6">
                                                    <div className="flex justify-between items-start mb-6">
                                                        <div className="flex items-center gap-4">
                                                            <div className={`flex items-center gap-2 px-3 py-1 rounded-lg ${statusConfig.bgColor} ${statusConfig.textColor} text-sm font-semibold`}>
                                                                <StatusIcon className={(abstract.status === 'in_review' || abstract.status === 'resubmitted') ? 'animate-spin' : ''} />
                                                                {statusConfig.label}
                                                            </div>
                                                            <div className="flex items-center gap-1 text-sm text-gray-500">
                                                                <FaCalendarAlt className="text-xs" />
                                                                <span>Assigned {getTimeSinceAssignment(abstract)}</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-wrap gap-4 mb-6">
                                                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl border-2 border-purple-200 flex-[2_1_250px] min-w-[250px]">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <FaBook className="text-purple-600" />
                                                                <p className="text-xs font-semibold text-gray-500">Abstract Title</p>
                                                            </div>
                                                            <p className="font-bold text-gray-900 text-sm line-clamp-2">{abstract.abstractTitle}</p>
                                                        </div>

                                                        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-4 rounded-xl border-2 border-teal-200 flex-[1_1_150px] min-w-[150px]">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <FaBook className="text-teal-600" />
                                                                <p className="text-xs font-semibold text-gray-500">Department</p>
                                                            </div>
                                                            <p className="font-bold text-gray-900 text-sm">{abstract.subject}</p>
                                                        </div>

                                                        <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-4 rounded-xl border-2 border-amber-200 flex-[1_1_150px] min-w-[150px]">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <FaGraduationCap className="text-amber-600" />
                                                                <p className="text-xs font-semibold text-gray-500">Stream</p>
                                                            </div>
                                                            <p className="font-bold text-gray-900 text-sm">{abstract.major}</p>
                                                        </div>

                                                        <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-4 rounded-xl border-2 border-indigo-200 flex-[1_1_150px] min-w-[150px]">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <FaBook className="text-indigo-600" />
                                                                <p className="text-xs font-semibold text-gray-500">Specialization</p>
                                                            </div>
                                                            <p className="font-bold text-gray-900 text-sm">{abstract.specialization}</p>
                                                        </div>

                                                        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-xl border-2 border-blue-200 flex-[1_1_180px] min-w-[180px]">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <FaUser className="text-blue-600" />
                                                                <p className="text-xs font-semibold text-gray-500">Author</p>
                                                            </div>
                                                            <p className="font-semibold text-gray-900 text-sm">{abstract.userName}</p>
                                                        </div>

                                                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl border-2 border-green-200 flex-[1_1_180px] min-w-[180px]">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <FaFileContract className="text-green-600" />
                                                                <p className="text-xs font-semibold text-gray-500">Content</p>
                                                            </div>
                                                            {abstract.uploadedFile ? (
                                                                <div className="space-y-2">
                                                                    <div className="flex items-center gap-2">
                                                                        {getFileIcon(abstract.uploadedFile.name)}
                                                                        <p className="text-xs font-bold text-gray-900 truncate" title={abstract.uploadedFile.name}>
                                                                            {abstract.uploadedFile.name}
                                                                        </p>
                                                                    </div>
                                                                    <div className="flex gap-2">
                                                                        <button
                                                                            onClick={() => handlePreviewClick(abstract)}
                                                                            className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-purple-500 text-white text-xs rounded hover:bg-purple-600 transition-all font-bold shadow-sm"
                                                                        >
                                                                            <FaEye className="text-xs" />
                                                                            View
                                                                        </button>
                                                                        {isDownloadableFile(abstract.uploadedFile.name) && (
                                                                            <button
                                                                                onClick={() => handleDownloadFile(abstract)}
                                                                                className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-all font-bold shadow-sm"
                                                                            >
                                                                                <FaDownload className="text-xs" />
                                                                                File
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ) : abstract.abstractText ? (
                                                                <div className="space-y-2">
                                                                    <p className="text-xs text-gray-600 line-clamp-1 italic">Text submission</p>
                                                                    <button
                                                                        onClick={() => handlePreviewClick(abstract)}
                                                                        className="w-full flex items-center justify-center gap-1 px-2 py-1 bg-purple-500 text-white text-xs rounded hover:bg-purple-600 transition-all font-bold shadow-sm"
                                                                    >
                                                                        <FaEye className="text-xs" />
                                                                        Read Abstract
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <p className="text-xs text-gray-400 italic">No content available</p>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-100">
                                                        <div className="flex flex-wrap gap-4 text-xs text-gray-600">
                                                            {abstract.submittedAt && (
                                                                <div className="flex items-center gap-1">
                                                                    <FaCalendarAlt />
                                                                    <span>Submitted: {formatDate(abstract.submittedAt)}</span>
                                                                </div>
                                                            )}
                                                            {abstract.inQueueAt && (
                                                                <div className="flex items-center gap-1">
                                                                    <FaCalendarAlt />
                                                                    <span>Assigned: {formatDate(abstract.inQueueAt)}</span>
                                                                </div>
                                                            )}
                                                            {abstract.acceptedAt && (
                                                                <div className="flex items-center gap-1">
                                                                    <FaCalendarAlt />
                                                                    <span>Accepted: {formatDate(abstract.acceptedAt)}</span>
                                                                </div>
                                                            )}
                                                            {abstract.rejectedAt && (
                                                                <div className="flex items-center gap-1">
                                                                    <FaCalendarAlt />
                                                                    <span>Rejected: {formatDate(abstract.rejectedAt)}</span>
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="flex gap-2">
                                                            {canAcceptForReview && (
                                                                <>
                                                                    <button
                                                                        onClick={() => handleActionClick(abstract, 'accept_for_review')}
                                                                        className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-all font-medium whitespace-nowrap"
                                                                    >
                                                                        <FaCheckCircle />
                                                                        Accept for Review
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleActionClick(abstract, 'reject_for_review')}
                                                                        className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-all font-medium whitespace-nowrap"
                                                                    >
                                                                        <FaTimesCircle />
                                                                        Reject for Review
                                                                    </button>
                                                                </>
                                                            )}
                                                            {canAcceptOrReject && (
                                                                <>
                                                                    <button
                                                                        onClick={() => handleActionClick(abstract, 'accept')}
                                                                        className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all font-medium whitespace-nowrap shadow-sm"
                                                                    >
                                                                        <FaCheckCircle />
                                                                        Accept
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleActionClick(abstract, 'reject')}
                                                                        className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all font-medium whitespace-nowrap shadow-sm"
                                                                    >
                                                                        <FaTimesCircle />
                                                                        Reject
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleActionClick(abstract, 'modify')}
                                                                        className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-all font-medium whitespace-nowrap shadow-sm"
                                                                    >
                                                                        <FaEdit />
                                                                        Modify
                                                                    </button>
                                                                </>
                                                            )}


                                                            {(abstract.status === 'accepted' || abstract.status === 'rejected') && (
                                                                <div className="px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
                                                                    <p className="text-sm text-gray-600 font-medium whitespace-nowrap">
                                                                        Review completed on {formatDate(abstract.acceptedAt || abstract.rejectedAt)}
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default AssignedAbstract;
